// Creative builders backed by browser-selected files and the creative API.
//
// CreativeBuilder lives here; each channel's editor is its own module
// (creative-meta-editor, creative-ctv-builder, creative-dsp-builder) and the
// required-field gate is `@/lib/creative-required-issues`. Everything this
// module exported before the split is still exported from it.

import { useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { Icons } from '@/components/primitives';
import { useCreatives } from '@/api/hooks';
import type { Creative } from '@/api/types';
import { useCampaignPlan, useMetaDeliveryContract, useRolloutCreative } from '@/api/manage-hooks';
import {
  MetaDeliveryPolicy,
  MetaDeliveryPolicyLoading,
} from '@/components/meta-delivery-policy';
import { RequiredField, useRequiredFeedback } from '@/components/required-field';
import { libraryToAssets } from '@/lib/creative-library-assets';
import { LibraryPicker } from './library-picker';
import { PinnedVersionBadge } from '@/components/pinned-version-badge';
import { CreativeSlotCard } from '@/components/creative-slot-card';
import { posterInitials } from '@/lib/creative-slot-view';
import { slotRollouts } from '@/lib/creative-rollouts';
import {
  creativeCollectionFieldKey,
  creativeRequiredIssues,
  metaAssetFieldKey,
  metaCopyFieldKey,
  metaDestinationFieldKey,
  metaDisplayUrlFieldKey,
  type CreativeBuilderProduction,
  type CreativeSlot,
  type CtvSlotData,
  type DspSlotData,
  type MetaAssetsBySlot,
  type MetaCreativeSlot,
  type MetaSlotAssets,
} from '@/lib/creative-required-issues';
import {
  CHANNEL_FORMATS,
  CHANNEL_FIELDS,
  META_COPY_SPEC_KEYS,
  metaDefaults,
  metaFieldsFromCreative,
  type ChannelConfig,
  type CreativeChannel,
} from './creative-config';
import { MetaCreativeEditor, defaultAdName } from './creative-meta-editor';
import { CtvCreativeBuilder, type CreativeRolloutBadgeProps } from './creative-ctv-builder';
import { DspGroupBuilder } from './creative-dsp-builder';

export interface CreativeBuilderProps {
  channel: CreativeChannel;
  production: CreativeBuilderProduction | null | undefined;
  /** The step is confirmed: the footer offers Edit instead of Confirm. */
  confirmed?: boolean;
  onConfirm?: () => void;
  onEdit?: () => void;
  /** The Meta slots; the Meta channel reads and writes them. */
  creatives: MetaCreativeSlot[];
  setCreatives: Dispatch<SetStateAction<MetaCreativeSlot[]>>;
  /** The Meta slots' media by slot id. Without them each editor keeps its own. */
  metaAssets?: MetaAssetsBySlot;
  setMetaAssets?: Dispatch<SetStateAction<MetaAssetsBySlot>>;
  /** The CTV slots and their data. Without them the CTV builder keeps its own. */
  ctvCreatives?: CreativeSlot[];
  setCtvCreatives?: Dispatch<SetStateAction<CreativeSlot[]>>;
  ctvData?: Record<string, CtvSlotData>;
  setCtvData?: Dispatch<SetStateAction<Record<string, CtvSlotData>>>;
  /** The Display banner groups and their data. Without them the Display builder keeps its own. */
  dspGroups?: CreativeSlot[];
  setDspGroups?: Dispatch<SetStateAction<CreativeSlot[]>>;
  dspData?: Record<string, DspSlotData>;
  setDspData?: Dispatch<SetStateAction<Record<string, DspSlotData>>>;
  /** One creative, no slot list and no confirm footer (the New Creative wizard). */
  singleMode?: boolean;
  /** A published campaign: slots can roll out a newer approved version. */
  locked?: boolean;
  campaignId?: string;
}

function CreativeBuilder({ channel, production, confirmed, onConfirm, onEdit, creatives, setCreatives, metaAssets, setMetaAssets, ctvCreatives, setCtvCreatives, ctvData, setCtvData, dspGroups, setDspGroups, dspData, setDspData, singleMode, locked = false, campaignId = '' }: CreativeBuilderProps) {
  const cfg: ChannelConfig = CHANNEL_FORMATS[channel] || CHANNEL_FORMATS.meta;
  const metaContractQuery = useMetaDeliveryContract(channel === 'meta');
  const fields = CHANNEL_FIELDS[channel];
  const [activeId, setActiveId] = useState(creatives?.[0]?.id || 'mc-1');
  const [metaPickerOpen, setMetaPickerOpen] = useState(false);
  // /creatives list used only to surface "N available" counts in the picker CTAs.
  const { data: libraryCreatives = [] } = useCreatives();
  // A published campaign's slots can roll a newer approved version out (0024).
  // The plan carries every rollout requested at this revision; the newest per
  // slot is what the badge shows, and the plan refetches as the queue writes.
  const planQuery = useCampaignPlan(locked ? campaignId : '');
  const rolloutCreative = useRolloutCreative();
  const rollouts = slotRollouts(planQuery.data);
  const rolloutFor = (position: number): CreativeRolloutBadgeProps => (locked && campaignId
    ? {
        campaignName: planQuery.data?.campaign?.name,
        provider: channel === 'meta' ? 'meta' : 'ttd',
        rollout: rollouts.get(position) ?? null,
        rolloutPending: rolloutCreative.isPending,
        onRollout: (toRevision: number) => {
          const slot = planQuery.data?.creatives?.find((c) => c.position === position);
          if (!slot) return;
          rolloutCreative.mutate({
            campaignId,
            position,
            creative_revision: toRevision,
            expected_creative_revision: slot.creative_revision,
          });
        },
      }
    : {});
  const availCount = (ch: CreativeChannel) => libraryCreatives.filter(c => c.channel === ch).length;
  const requiredFeedback = useRequiredFeedback();
  const currentRequiredIssues = creativeRequiredIssues({
    channel,
    production,
    creatives,
    metaAssets,
    ctvCreatives,
    ctvData,
    dspGroups,
    dspData,
  });
  const creativeComplete = currentRequiredIssues.length === 0;
  const unresolvedKeySignature = currentRequiredIssues.map((issue) => issue.key).join('|');

  useEffect(() => {
    const unresolved = new Set(currentRequiredIssues.map((issue) => issue.key));
    Object.keys(requiredFeedback.messages).forEach((key) => {
      if (!unresolved.has(key)) requiredFeedback.clear(key);
    });
  }, [unresolvedKeySignature, requiredFeedback.messages, requiredFeedback.clear]);

  const confirmCreative = () => {
    const issues = creativeRequiredIssues({
      channel,
      production,
      creatives,
      metaAssets,
      ctvCreatives,
      ctvData,
      dspGroups,
      dspData,
    });
    if (channel === 'meta' && issues.length > 0) {
      // Copy keys belong in here too: with no seeded copy, a missing headline is
      // the ordinary first state of a new creative, and the tab has to follow the
      // creative the message is about.
      const firstMissingCreative = creatives.find((creative) =>
        issues.some((issue) =>
          issue.key === metaDestinationFieldKey(creative.id)
          || issue.key === metaDisplayUrlFieldKey(creative.id)
          || META_COPY_SPEC_KEYS.some((key) => issue.key === metaCopyFieldKey(creative.id, key))
          || CHANNEL_FORMATS.meta.formats.some((format) =>
            issue.key === metaAssetFieldKey(creative.id, format.key || format.name))));
      if (firstMissingCreative) setActiveId(firstMissingCreative.id);
    }
    if (!requiredFeedback.report(issues)) return;
    onConfirm?.();
  };

  const addCreative = () => {
    const n = creatives.length + 1;
    const id = `mc-${Date.now()}`;
    setCreatives([...creatives, { id, label: `Creative ${n}`, metaFields: {} }]);
    setActiveId(id);
  };
  const removeCreative = (id: string) => {
    if (creatives.length <= 1) return;
    const next = creatives.filter(c => c.id !== id);
    setCreatives(next);
    const first = next[0];
    if (activeId === id && first) setActiveId(first.id);
  };

  return (
    <div>
      {/* The channel, plainly. A "DEFAULTS" badge headed this line after the default cards it
          introduced were removed (nothing here enforces a default). */}
      <div className="creative-channel-heading mb-2" style={{fontSize:12.5}}>
        <b>{cfg.name}</b>
        <span className="muted"> · {cfg.blurb}</span>
      </div>
      {channel === 'meta' && metaContractQuery.isPending && <MetaDeliveryPolicyLoading />}
      {/* Meta's delivery policy is read from GET /meta/delivery-contract. CTV and
          Display used to render a hardcoded list here as cards headed "Locked
          default" with a green check — inventory, frequency caps, viewability
          floors — none of which this product enforces. Showing nothing is
          honest; a truthful TTD contract is a server change. */}
      <MetaDeliveryPolicy contract={channel === 'meta' ? metaContractQuery.data : null} scopes={['creative']} />

      {channel === 'meta' && !singleMode && (
        <>
          <div className="creative-slot-intro">
            <p>Each creative becomes one ad in every ad set. Upload the sizes you have, or pick an approved creative from the library; the one you are editing is outlined.</p>
            <div style={{display:'flex',gap:8,flexShrink:0}}>
              {/* Offered only with something to pick: it opened an empty picker (F5 UX P8). */}
              <button type="button" className="btn" disabled={availCount('meta') === 0} onClick={()=>setMetaPickerOpen(true)} title="Pick existing Meta creatives from the library">
                {availCount('meta') === 0 ? 'Library · no Meta creatives yet' : `Pick from library · ${availCount('meta')} available`}
              </button>
              <button type="button" className="btn btn-primary" onClick={addCreative} title="Add another creative">
                New creative
              </button>
            </div>
          </div>
          <div className="creative-slot-list creative-slot-list--draft" style={{marginBottom:4}}>
            {creatives.map((c) => {
              const cAssets: MetaSlotAssets = metaAssets?.[c.id] || {};
              const name = c.adName || defaultAdName(production, c.label, 'meta');
              const posterAsset = ['fs','sq','story'].map((key) => cAssets[key]).find((asset) => asset?.url);
              const posterKey = ['fs','sq','story'].find((key) => cAssets[key]?.url);
              const ratio: Record<string, string> = { sq: '1:1', fs: '4:5', story: '9:16' };
              const sizes = cfg.formats.map((format) => ({ key: format.key, label: ratio[format.key] ?? format.name, present: !!cAssets[format.key] }));
              const missingStory = !cAssets.story && (cAssets.sq || cAssets.fs);
              const fields = c.metaFields || {};
              const isVideo = Object.values(cAssets).some((asset) => asset?.type === 'video' || asset?.mediaKind === 'video');
              const copy: Array<[string, string | undefined]> = [
                ['Headline', fields.headline],
                ['Post text', fields.post_text],
                ['Destination', fields.destination_url || fields.dest_url],
                ['Call to action', fields.cta],
              ];
              return (
                <div key={c.id} className={activeId === c.id ? 'is-active' : undefined}>
                <CreativeSlotCard
                  ariaLabel={`Creative slot: ${name}`}
                  name={name}
                  poster={posterAsset ? { url: posterAsset.url, kind: isVideo ? 'video' : 'image', ratio: ratio[posterKey ?? ''] ?? '' } : null}
                  initials={posterInitials(name)}
                  isVideo={isVideo}
                  mediaLabel={isVideo ? 'Video' : 'Image'}
                  formatLabel=""
                  versionLabel={c.fromLibrary ? `From library · v${c.fromLibrary.revision}` : 'New · draft'}
                  sizes={sizes}
                  sizesNote={c.fromLibrary
                    ? 'Pinned to the library version. Uploading here creates a new creative; the library one stays as it is.'
                    : missingStory
                      ? 'Without a 9:16 this creative runs in feeds only. Stories, Reels and Explore stay off for it.'
                      : undefined}
                  provider="meta"
                  tone="quiet"
                  aside={
                    <>
                      <span className="creative-slot-eyebrow">Copy</span>
                      <dl className="creative-slot-lines">
                        {copy.map(([label, value]) => (
                          <div key={label} className="creative-slot-line">
                            <dt>{label}</dt>
                            <dd style={value ? undefined : {color:'var(--bad)'}}>{value || 'Required · not written'}</dd>
                          </div>
                        ))}
                      </dl>
                    </>
                  }
                  actions={
                    <>
                      <button type="button" className={`btn btn-sm${activeId === c.id ? ' btn-primary' : ''}`} aria-label={`Edit creative ${name}`} aria-pressed={activeId === c.id} onClick={()=>setActiveId(c.id)}>
                        {activeId === c.id ? 'Editing' : 'Edit'}
                      </button>
                      {creatives.length > 1 && (
                        <button type="button" className="btn btn-sm btn-ghost" aria-label={`Remove creative ${name}`} onClick={()=>removeCreative(c.id)}>
                          Remove
                        </button>
                      )}
                    </>
                  }
                />
                </div>
              );
            })}
          </div>
          <div className="creative-slot-editing">
            <h4>Editing · {creatives.find((c) => c.id === activeId)?.adName || defaultAdName(production, creatives.find((c) => c.id === activeId)?.label || 'Creative', 'meta')}</h4>
          </div>
        </>
      )}

      {channel === 'meta' && (
        <LibraryPicker open={metaPickerOpen} channel="meta" currentShow={production} multi onClose={()=>setMetaPickerOpen(false)} onPick={(list: Creative[])=>{
          if (!list || !list.length) return;
          const newCreatives = [...creatives];
          const nowTs = Date.now();
          const newAssetsMap = { ...(metaAssets || {}) };

          // If the currently active creative is empty (no assets uploaded), replace it
          // with the first library pick instead of appending on top.
          const activeIdx = newCreatives.findIndex(c => c.id === activeId);
          const active = newCreatives[activeIdx];
          const activeAssetsCur = active ? metaAssets?.[active.id] : null;
          const activeIsEmpty = !!active && (!activeAssetsCur || Object.values(activeAssetsCur).every(v => !v));

          let startIdx = 0;
          let firstInsertedId: string | null = null;
          const first = list[0];
          if (activeIsEmpty && first) {
            const cr = first;
            const oldId = active.id;
            const id = `mc-lib-${cr.id}-${nowTs}-0`;
            newCreatives[activeIdx] = {
              id,
              label: active.label,
              adName: cr.name,
              metaFields: metaFieldsFromCreative(cr),
              fromLibrary: { id: cr.id, name: cr.name, revision: cr.approved_revision ?? cr.revision },
            };
            delete newAssetsMap[oldId];
            newAssetsMap[id] = libraryToAssets(cr, production, 'meta');
            firstInsertedId = id;
            startIdx = 1;
          }

          for (let i = startIdx; i < list.length; i++) {
            const cr = list[i];
            if (!cr) continue;
            const id = `mc-lib-${cr.id}-${nowTs}-${i}`;
            newCreatives.push({
              id,
              label: `Creative ${newCreatives.length + 1}`,
              adName: cr.name,
              metaFields: metaFieldsFromCreative(cr),
              fromLibrary: { id: cr.id, name: cr.name, revision: cr.approved_revision ?? cr.revision },
            });
            newAssetsMap[id] = libraryToAssets(cr, production, 'meta');
            if (!firstInsertedId) firstInsertedId = id;
          }

          setCreatives(newCreatives);
          setMetaAssets?.(newAssetsMap);
          setActiveId(firstInsertedId || newCreatives[newCreatives.length - 1]?.id || activeId);
        }}/>
      )}

      <RequiredField
        fieldKey={creativeCollectionFieldKey(channel)}
        message={requiredFeedback.messages[creativeCollectionFieldKey(channel)]}
        attempt={requiredFeedback.attempt}
        surface
      >
        {channel === 'ctv' ? (
          <CtvCreativeBuilder cfg={CHANNEL_FORMATS.ctv} production={production} creatives={ctvCreatives} setCreatives={setCtvCreatives} data={ctvData} setData={setCtvData} singleMode={singleMode} availableCount={availCount('ctv')} requiredFeedback={requiredFeedback} locked={locked} rolloutFor={rolloutFor}/>
        ) : channel === 'dsp' ? (
          <DspGroupBuilder cfg={CHANNEL_FORMATS.dsp} production={production} groups={dspGroups} setGroups={setDspGroups} data={dspData} setData={setDspData} singleMode={singleMode} availableCount={availCount('dsp')} requiredFeedback={requiredFeedback} locked={locked} rolloutFor={rolloutFor}/>
        ) : (
          creatives.map((c, i) => {
            const cid = c.id;
            const cAssets: MetaSlotAssets = metaAssets?.[cid] || Object.fromEntries(cfg.formats.map(f => [f.key || f.name, null]));
            const cSetAssets = (updater: SetStateAction<MetaSlotAssets>) => setMetaAssets?.(prev => {
              const cur = prev?.[cid] || Object.fromEntries(cfg.formats.map(f => [f.key || f.name, null]));
              const next = typeof updater === 'function' ? updater(cur) : updater;
              return { ...(prev||{}), [cid]: next };
            });
            // Uploading into a slot that came from the library does not edit that
            // creative — `campaignCreativeSelections` only routes a slot to
            // `libraryIds` when it carries no local file, so a file here mints a
            // NEW creative and the pin is dropped. That is a defensible thing to
            // want and a terrible thing to discover afterwards, so say it.
            const pin = c.fromLibrary;
            const replacesLibraryPin = !!pin
              && Object.values(cAssets).some((asset) => !!asset?.file);
            return (
              <div key={c.id} style={{display: activeId===c.id ? 'block' : 'none'}}>
                {replacesLibraryPin && (
                  <div className="facet" role="status" style={{marginBottom:12,borderColor:'var(--warn)'}}>
                    Uploading here creates a new creative and replaces{' '}
                    <strong>{pin.name}</strong> in this campaign.{' '}
                    {pin.name} itself is not changed — remove the upload to keep it.
                  </div>
                )}
                <MetaCreativeEditor
                  channel={channel}
                  production={production}
                  cfg={cfg}
                  fields={fields}
                  initialVals={metaDefaults(production, channel, cfg.name, i===0 ? '' : c.label)}
                  formValues={c.metaFields || {}}
                  setFormValues={(metaFields)=>setCreatives(prev => prev.map(cc => cc.id===c.id ? {...cc, metaFields} : cc))}
                  assets={metaAssets ? cAssets : undefined}
                  setAssets={metaAssets ? cSetAssets : undefined}
                  adName={c.adName ?? defaultAdName(production, c.label, 'meta')}
                  setAdName={(v)=>setCreatives(prev => prev.map(cc => cc.id===c.id ? {...cc, adName: v} : cc))}
                  adNameHint={pin ? `from library: ${pin.name}` : null}
                  libraryRef={pin ? (
                    <PinnedVersionBadge
                      creative={libraryCreatives.find(lc => lc.id === pin.id)}
                      pinnedRevision={pin.revision}
                      campaignLive={locked}
                      {...rolloutFor(i)}
                    />
                  ) : null}
                  creativeId={c.id}
                  requiredFeedback={requiredFeedback}
                />
              </div>
            );
          })
        )}
      </RequiredField>

      {!singleMode && (
      <div className="flex between items-center mt-3" style={{gap:10}}>
        <div className="muted" style={{fontSize:11.5}}>
          {confirmed
            ? <><span style={{color:'var(--good)'}}>{Icons.check}</span> {channel==='meta' && creatives.length>1 ? `All ${creatives.length} creatives confirmed.` : 'Creative confirmed.'} {locked ? 'Pinned to the published plan.' : 'Edit to swap assets.'}</>
            : !creativeComplete
              ? 'Complete every highlighted required field before confirming this creative.'
            : (channel==='meta' && creatives.length>1
                ? `Upload assets for all ${creatives.length} creatives, then confirm to continue.`
                : 'Upload your assets and review, then confirm to continue.')}
        </div>
        {confirmed
          ? <button className="btn btn-sm" disabled={locked} onClick={onEdit}>Edit creative</button>
          : <button className="btn btn-primary" onClick={confirmCreative}>{Icons.check} Confirm {channel==='meta' && creatives.length>1 ? `${creatives.length} creatives` : 'creative'}</button>}
      </div>
      )}
    </div>
  );
}

export { CreativeBuilder, MetaCreativeEditor, CtvCreativeBuilder, DspGroupBuilder };
export { CHANNEL_FORMATS, CHANNEL_FIELDS, metaDefaults } from './creative-config';
export { creativeRequiredIssues };
export type {
  CreativeBuilderProduction,
  CreativeSlot,
  CtvSlotData,
  DspSlotData,
  MetaAssetsBySlot,
  MetaCreativeSlot,
  MetaSlotAssets,
};
